/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  message,
  Card,
  Row,
  Col,
  Tag,
  Button,
  Spin,
  Alert,
  Descriptions,
  Table,
  InputNumber,
  Modal,
  Form,
  TimePicker,
  Input,
  Select,
  Divider,
  Tooltip,
  DatePicker,
  Space,
  Drawer,
  List,
  Avatar,
  Badge,
  Typography,
  Empty,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckOutlined,
  StopOutlined,
  SaveOutlined,
  CloseOutlined,
  DollarOutlined,
  TeamOutlined,
  PlusOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { tripsApi, schedulesApi, pricingApi, boatApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

// ══════════════════════════════════════════════════════════════════════════════
export const TripDetail = () => {
  const { id } = useParams();
  const history = useHistory();

  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trip, setTrip] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [schedLoading, setSchedLoading] = useState(false);
  const [decks, setDecks] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedScheduleForMobile, setSelectedScheduleForMobile] = useState(
    null,
  );
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // ── Deck pricing inline edit ──────────────────────────────────────────────
  const [editingDecks, setEditingDecks] = useState({});
  const [savingDeckId, setSavingDeckId] = useState(null);

  // ── Date time edit modal ──────────────────────────────────────────────────
  const [dateModal, setDateModal] = useState(false);
  const [dateTarget, setDateTarget] = useState(null);
  const [dateLoading, setDateLoading] = useState(false);
  const [dateForm] = Form.useForm();

  // ── Add dates modal ───────────────────────────────────────────────────────
  const [addDateModal, setAddDateModal] = useState(false);
  const [addDateLoading, setAddDateLoading] = useState(false);
  const [addDateType, setAddDateType] = useState("single");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [addDateNotes, setAddDateNotes] = useState("");
  const [addDateForm] = Form.useForm();

  // ── Date pricing overrides ────────────────────────────────────────────────
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [datePricings, setDatePricings] = useState({});
  const [pricingLoading, setPricingLoading] = useState(null);
  const [addModal, setAddModal] = useState({ open: false, tripDateId: null });
  const [addForm, setAddForm] = useState({ deckId: "", price: "" });
  const [addLoading, setAddLoading] = useState(false);
  const [delLoadingKey, setDelLoadingKey] = useState(null);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load user permissions
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr) {
          setUserPermissions([]);
          setIsCheckingPermission(false);
          return;
        }

        const userData = JSON.parse(userStr);

        let permissions = [];

        if (userData.permissions && Array.isArray(userData.permissions)) {
          permissions = userData.permissions;
        } else if (userData.role && userData.role.permissions) {
          permissions = userData.role.permissions;
        }

        const normalizedPermissions = permissions.map((p) =>
          typeof p === "string" ? p : p.name,
        );

        setUserPermissions(normalizedPermissions);
        console.log("TripDetail - User permissions:", normalizedPermissions);
      } catch (error) {
        console.error("Error loading user permissions:", error);
        setUserPermissions([]);
      } finally {
        setIsCheckingPermission(false);
      }
    };

    loadUserPermissions();
  }, []);

  // Check permissions using standardized utility
  const canViewTrips = hasPermission(userPermissions, "trips.view");
  const canEditTrips = hasPermission(userPermissions, "trips.edit");
  const canDeleteTrips = hasPermission(userPermissions, "trips.delete");
  const canViewSchedules = hasPermission(userPermissions, "schedules.view");
  const canCreateSchedules = hasPermission(userPermissions, "schedules.create");
  const canEditSchedules = hasPermission(userPermissions, "schedules.edit");
  const canDeleteSchedules = hasPermission(userPermissions, "schedules.delete");
  const canViewPricing = hasPermission(userPermissions, "pricing.view");
  const canCreatePricing = hasPermission(userPermissions, "pricing.create");
  const canDeletePricing = hasPermission(userPermissions, "pricing.delete");

  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (canViewTrips && id) {
      fetchAll();
    }
  }, [id, canViewTrips]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [r, d] = await Promise.all([
        tripsApi.getById(id),
        boatApi.getAllDecks().catch(() => []),
      ]);
      setTrip(r);
      setDecks(Array.isArray(d) ? d : []);
      if (canViewSchedules) {
        fetchSchedules();
      }
    } catch (error) {
      if (error.response?.status === 403) {
        message.error("You don't have permission to view this trip");
      } else {
        message.error("Failed to load trip");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      setSchedLoading(true);
      const r = await schedulesApi.getByTrip(id, {
        limit: isMobile ? 50 : 200,
      });
      setSchedules(r.data || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        message.error("Failed to load schedules");
      }
    } finally {
      setSchedLoading(false);
    }
  };

  // ── Trip status ───────────────────────────────────────────────────────────
  const handleActivate = async () => {
    if (!canEditTrips) {
      message.error("You don't have permission to activate trips");
      return;
    }

    try {
      await tripsApi.activate(id);
      message.success("Trip activated");
      fetchAll();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to activate trips");
      } else {
        message.error(e.response?.data?.message || "Failed");
      }
    }
  };

  const handleDeactivate = async () => {
    if (!canDeleteTrips) {
      message.error("You don't have permission to delete trips");
      return;
    }

    try {
      await tripsApi.remove(id);
      message.success("Trip deleted");
      history.push("/trips");
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to delete this trip");
      } else {
        message.error(e.response?.data?.message || "Failed");
      }
    }
  };

  // ── Schedule cancel / restore ─────────────────────────────────────────────
  const handleCancelDate = async (dateId) => {
    if (!canEditSchedules && !canDeleteSchedules) {
      message.error("You don't have permission to cancel schedules");
      return;
    }

    try {
      await schedulesApi.cancel(dateId);
      message.success("Date cancelled");
      fetchSchedules();
      if (mobileDrawerOpen) setMobileDrawerOpen(false);
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to cancel this schedule");
      } else {
        message.error(e.response?.data?.message || "Failed");
      }
    }
  };

  const handleRestoreDate = async (dateId) => {
    if (!canEditSchedules) {
      message.error("You don't have permission to restore schedules");
      return;
    }

    try {
      await schedulesApi.restore(dateId);
      message.success("Date restored");
      fetchSchedules();
      if (mobileDrawerOpen) setMobileDrawerOpen(false);
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to restore this schedule");
      } else {
        message.error(e.response?.data?.message || "Failed");
      }
    }
  };

  // ── Deck pricing inline edit ──────────────────────────────────────────────
  const startEditDeck = (td) => {
    if (!canEditTrips) {
      message.error("You don't have permission to edit deck pricing");
      return;
    }
    setEditingDecks((prev) => ({
      ...prev,
      [td.id]: {
        adultPrice: Number(td.adultPrice),
        childPrice: Number(td.childPrice),
        capacity: Number(td.capacity),
      },
    }));
  };

  const cancelEditDeck = (tdId) => {
    setEditingDecks((prev) => {
      const n = { ...prev };
      delete n[tdId];
      return n;
    });
  };

  const saveDeckPricing = async (td) => {
    if (!canEditTrips) {
      message.error("You don't have permission to edit deck pricing");
      return;
    }

    const vals = editingDecks[td.id];
    if (!vals?.adultPrice || vals.adultPrice <= 0)
      return message.error("Adult price must be > 0");
    if (!vals?.childPrice || vals.childPrice <= 0)
      return message.error("Child price must be > 0");
    if (!vals?.capacity || vals.capacity < 1)
      return message.error("Capacity must be ≥ 1");
    try {
      setSavingDeckId(td.id);
      await tripsApi.update(id, {
        deckPricing: [
          {
            deckId: td.deckId,
            adultPrice: vals.adultPrice,
            childPrice: vals.childPrice,
            capacity: vals.capacity,
          },
        ],
      });
      message.success(`${td.deck?.name} pricing updated`);
      cancelEditDeck(td.id);
      fetchAll();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to edit deck pricing");
      } else {
        message.error(e.response?.data?.message || "Failed to save");
      }
    } finally {
      setSavingDeckId(null);
    }
  };

  // ── Date time modal ───────────────────────────────────────────────────────
  const openDateModal = (record) => {
    if (!canEditSchedules) {
      message.error("You don't have permission to edit schedule times");
      return;
    }
    setDateTarget(record);
    dateForm.setFieldsValue({
      startTime: record.startTime
        ? dayjs(record.startTime, "HH:mm")
        : trip?.startTime
        ? dayjs(trip.startTime, "HH:mm")
        : null,
      endTime: record.endTime
        ? dayjs(record.endTime, "HH:mm")
        : trip?.endTime
        ? dayjs(trip.endTime, "HH:mm")
        : null,
      notes: record.notes || "",
    });
    setDateModal(true);
  };

  const handleSaveDate = async () => {
    if (!canEditSchedules) {
      message.error("You don't have permission to edit schedule times");
      setDateModal(false);
      return;
    }

    try {
      const vals = await dateForm.validateFields();
      setDateLoading(true);
      await schedulesApi.updateDate(dateTarget.id, {
        startTime: vals.startTime ? vals.startTime.format("HH:mm") : undefined,
        endTime: vals.endTime ? vals.endTime.format("HH:mm") : undefined,
        notes: vals.notes || undefined,
      });
      message.success(`Date ${dateTarget.date} updated`);
      setDateModal(false);
      setDateTarget(null);
      dateForm.resetFields();
      fetchSchedules();
    } catch (e) {
      if (e.errorFields) return;
      if (e.response?.status === 403) {
        message.error("You don't have permission to edit schedule times");
      } else {
        message.error(e.response?.data?.message || "Failed to update date");
      }
    } finally {
      setDateLoading(false);
    }
  };

  // ── Add dates functionality ───────────────────────────────────────────────
  const handleAddDates = async () => {
    if (!canCreateSchedules) {
      message.error("You don't have permission to add schedule dates");
      setAddDateModal(false);
      return;
    }

    try {
      setAddDateLoading(true);

      if (addDateType === "single" && selectedDate) {
        const dateStr = selectedDate.format("YYYY-MM-DD");
        await schedulesApi.addSingleDate(id, dateStr, addDateNotes);
        message.success(`Date ${dateStr} added successfully`);
      } else if (addDateType === "range" && selectedDateRange) {
        const startDate = selectedDateRange[0].format("YYYY-MM-DD");
        const endDate = selectedDateRange[1].format("YYYY-MM-DD");
        const result = await schedulesApi.addDateRange(
          id,
          startDate,
          endDate,
          addDateNotes,
        );

        if (result.addedCount === result.requestedCount) {
          message.success(`Successfully added ${result.addedCount} dates`);
        } else {
          message.warning(
            `Added ${result.addedCount} of ${result.requestedCount} dates. ` +
              `${result.skippedCount} date(s) already existed.`,
          );
        }
      } else {
        message.error("Please select a date or date range");
        return;
      }

      setAddDateModal(false);
      setSelectedDate(null);
      setSelectedDateRange(null);
      setAddDateNotes("");
      addDateForm.resetFields();
      fetchSchedules();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to add schedule dates");
      } else {
        message.error(e.response?.data?.message || "Failed to add dates");
      }
    } finally {
      setAddDateLoading(false);
    }
  };

  // ── Date pricing load / expand ────────────────────────────────────────────
  const loadDatePricings = async (tripDateId) => {
    if (!canViewPricing) return;

    try {
      setPricingLoading(tripDateId);
      const r = await pricingApi.getDatePricings(tripDateId);
      setDatePricings((prev) => ({
        ...prev,
        [tripDateId]: Array.isArray(r) ? r : [],
      }));
    } catch {
      message.error("Failed to load price overrides");
    } finally {
      setPricingLoading(null);
    }
  };

  const handleTogglePricing = async (tripDateId) => {
    if (!canViewPricing) {
      message.error("You don't have permission to view pricing");
      return;
    }

    if (expandedKeys.includes(tripDateId)) {
      setExpandedKeys((prev) => prev.filter((k) => k !== tripDateId));
    } else {
      setExpandedKeys((prev) => [...prev, tripDateId]);
      if (!datePricings[tripDateId]) {
        await loadDatePricings(tripDateId);
      }
    }
  };

  const reloadDatePricings = async (tripDateId) => {
    if (!canViewPricing) return;

    try {
      const r = await pricingApi.getDatePricings(tripDateId);
      setDatePricings((prev) => ({
        ...prev,
        [tripDateId]: Array.isArray(r) ? r : [],
      }));
      fetchSchedules();
    } catch {
      /* silent */
    }
  };

  // ── Add price override ────────────────────────────────────────────────────
  const openAddModal = (tripDateId) => {
    if (!canCreatePricing) {
      message.error("You don't have permission to add price overrides");
      return;
    }
    setAddForm({ deckId: "", adultPrice: "", childPrice: "" });
    setAddModal({ open: true, tripDateId });
  };

  const handleAddPricing = async () => {
    if (!canCreatePricing) {
      message.error("You don't have permission to add price overrides");
      setAddModal({ open: false, tripDateId: null });
      return;
    }

    const tripDateId = addModal.tripDateId;
    if (!addForm.deckId) return message.error("Select a deck");
    if (!addForm.adultPrice || Number(addForm.adultPrice) <= 0)
      return message.error("Enter a valid adult price");
    if (!addForm.childPrice || Number(addForm.childPrice) <= 0)
      return message.error("Enter a valid child price");
    try {
      setAddLoading(true);
      await pricingApi.setDatePricing(tripDateId, {
        deckId: addForm.deckId,
        adultPrice: Number(addForm.adultPrice),
        childPrice: Number(addForm.childPrice),
      });
      message.success("Price override saved");
      setAddModal({ open: false, tripDateId: null });
      setAddForm({ deckId: "", price: "" });
      reloadDatePricings(tripDateId);
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to add price overrides");
      } else {
        message.error(e.response?.data?.message || "Failed to save override");
      }
    } finally {
      setAddLoading(false);
    }
  };

  // ── Delete price override ─────────────────────────────────────────────────
  const handleDeletePricing = async (tripDateId, deckId) => {
    if (!canDeletePricing) {
      message.error("You don't have permission to delete price overrides");
      return;
    }

    const key = `${tripDateId}_${deckId}`;
    try {
      setDelLoadingKey(key);
      await pricingApi.deleteDatePricing(tripDateId, deckId);
      message.success("Override removed — trip default price applies");
      reloadDatePricings(tripDateId);
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to delete price overrides");
      } else {
        message.error(e.response?.data?.message || "Failed to remove");
      }
    } finally {
      setDelLoadingKey(null);
    }
  };

  const availableDecks = (tripDateId) => {
    const overridden = new Set(
      (datePricings[tripDateId] || []).map((p) => p.deckId),
    );
    const base = (trip?.tripDecks || []).map((td) => ({
      id: td.deckId,
      name: td.deck?.name,
      adultPrice: td.adultPrice,
      childPrice: td.childPrice,
    }));
    return base.filter((d) => !overridden.has(d.id));
  };

  const isDateExists = (date) => {
    return schedules.some((s) => s.date === date.format("YYYY-MM-DD"));
  };

  // ── Mobile schedule card component ────────────────────────────────────────
  const ScheduleCard = ({ schedule, onPress }) => {
    const hasOverrides = (schedule.deckPricings || []).length > 0;
    const timeDisplay =
      schedule.startTime && schedule.endTime
        ? `${schedule.startTime} – ${schedule.endTime}`
        : `${trip?.startTime} – ${trip?.endTime}`;

    return (
      <Card
        style={{
          marginBottom: 12,
          borderRadius: 12,
          cursor: "pointer",
          transition: "all 0.3s ease",
        }}
        hoverable
        onClick={() => onPress(schedule)}
        bodyStyle={{ padding: "12px" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <Text strong style={{ fontSize: 16 }}>
                {schedule.date}
              </Text>
              <Tag color={schedule.status === "active" ? "green" : "red"}>
                {schedule.status?.toUpperCase()}
              </Tag>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <ClockCircleOutlined style={{ fontSize: 12, color: "#8c8c8c" }} />
              <Text style={{ fontSize: 12 }}>{timeDisplay}</Text>
            </div>
            {schedule.notes && (
              <Text
                type="secondary"
                style={{ fontSize: 11, display: "block", marginTop: 4 }}
              >
                {schedule.notes}
              </Text>
            )}
            {schedule.notes && schedule.notes.includes("Manually added") && (
              <Tag color="purple" style={{ marginTop: 6, fontSize: 10 }}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                Manually Added
              </Tag>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <Badge
              count={hasOverrides ? schedule.deckPricings?.length || 0 : 0}
              showZero={false}
              style={{ backgroundColor: "#d97706" }}
            />
            <DollarOutlined
              style={{
                fontSize: 20,
                color: hasOverrides ? "#d97706" : "#9ca3af",
                marginTop: 8,
                display: "block",
              }}
            />
          </div>
        </div>
      </Card>
    );
  };

  // ── Mobile schedule detail drawer ─────────────────────────────────────────
  const ScheduleDetailDrawer = ({ schedule, open, onClose }) => {
    const canEdit = canEditSchedules;
    const canCancel = canEditSchedules || canDeleteSchedules;
    const timeDisplay =
      schedule?.startTime && schedule?.endTime
        ? `${schedule?.startTime} – ${schedule?.endTime}`
        : `${trip?.startTime} – ${trip?.endTime}`;
    const hasOverrides = (schedule?.deckPricings || []).length > 0;
    const overrides = schedule?.deckPricings || [];
    const isManuallyAdded =
      schedule?.notes && schedule?.notes.includes("Manually added");

    return (
      <Drawer
        title={
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {schedule?.date}
            </Title>
            <Tag
              color={schedule?.status === "active" ? "green" : "red"}
              style={{ marginTop: 6 }}
            >
              {schedule?.status?.toUpperCase()}
            </Tag>
          </div>
        }
        placement="bottom"
        onClose={onClose}
        open={open}
        height="auto"
        closable={true}
        bodyStyle={{ padding: "16px" }}
      >
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <ClockCircleOutlined style={{ color: "#7B4D12" }} />
            <Text strong>Schedule Time</Text>
          </div>
          <Card size="small" style={{ background: "#f8fafc", borderRadius: 8 }}>
            <Text>{timeDisplay}</Text>
            {schedule?.startTime && schedule?.endTime && (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  (Overrides trip default times)
                </Text>
              </div>
            )}
          </Card>
        </div>

        {isManuallyAdded && (
          <div style={{ marginBottom: 16 }}>
            <Tag color="purple" style={{ marginBottom: 8 }}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              Manually Added Date
            </Tag>
            {schedule?.notes && schedule.notes !== "Manually added" && (
              <Text
                type="secondary"
                style={{ fontSize: 12, display: "block", marginTop: 4 }}
              >
                {schedule.notes}
              </Text>
            )}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <DollarOutlined style={{ color: "#d97706" }} />
            <Text strong>Price Overrides</Text>
            {hasOverrides && (
              <Badge
                count={overrides.length}
                style={{ backgroundColor: "#d97706" }}
              />
            )}
          </div>
          {overrides.length === 0 ? (
            <Alert
              message="No overrides for this date"
              description="All decks use trip-level prices"
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
            />
          ) : (
            overrides.map((override) => (
              <Card
                key={override.id || override.deckId}
                size="small"
                style={{ marginBottom: 8, borderRadius: 8 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Text strong>{override.deck?.name}</Text>
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>
                      Override prices
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <Tag
                      color="orange"
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      A: EGP {Number(override.adultPrice).toLocaleString()}
                    </Tag>
                    <Tag
                      color="cyan"
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        display: "block",
                      }}
                    >
                      C: EGP {Number(override.childPrice).toLocaleString()}
                    </Tag>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {canCreatePricing && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => {
              onClose();
              openAddModal(schedule?.id);
            }}
            style={{ width: "100%", marginBottom: 16 }}
          >
            Add Price Override
          </Button>
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
          {canEdit && (
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                onClose();
                openDateModal(schedule);
              }}
              style={{ flex: 1 }}
            >
              Edit Time
            </Button>
          )}
          {canCancel &&
            (schedule?.status === "active" ? (
              <Button
                danger
                icon={<StopOutlined />}
                onClick={() => handleCancelDate(schedule?.id)}
                style={{ flex: 1 }}
              >
                Cancel Date
              </Button>
            ) : (
              <Button
                icon={<CheckOutlined />}
                onClick={() => handleRestoreDate(schedule?.id)}
                style={{ flex: 1 }}
              >
                Restore Date
              </Button>
            ))}
        </div>
      </Drawer>
    );
  };

  // ── Expanded row content (pricing panel) for desktop ─────────────────────────────────
  const renderPricingPanel = (record) => {
    const tripDateId = record.id;
    const isLoading = pricingLoading === tripDateId;
    const rows = datePricings[tripDateId] || [];

    const defaults = Object.fromEntries(
      (trip?.tripDecks || []).map((td) => [
        td.deckId,
        {
          name: td.deck?.name,
          adultPrice: td.adultPrice,
          childPrice: td.childPrice,
        },
      ]),
    );

    const isManuallyAdded =
      record.notes && record.notes.includes("Manually added");

    return (
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #F5ECD7",
          borderRadius: 10,
          padding: isMobile ? "12px 16px" : "16px 20px",
          margin: "2px 0 8px 40px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 14,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: "#7B4D12", fontSize: 13 }}>
              <DollarOutlined style={{ marginRight: 6 }} />
              Date Price Overrides — {record.date}
              {isManuallyAdded && (
                <Tag color="purple" style={{ marginLeft: 8, fontSize: 11 }}>
                  Manually Added
                </Tag>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
              Overrides apply to this date only. Unset decks use the trip-level
              price.
            </div>
          </div>
          {canCreatePricing && !isMobile && (
            <Button
              type="primary"
              style={{ backgroundColor: "#7B4D12", borderColor: "#7B4D12" }}
              size="small"
              icon={<PlusOutlined />}
              onClick={() => openAddModal(tripDateId)}
            >
              Add Override
            </Button>
          )}
        </div>

        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Spin size="small" />
          </div>
        ) : rows.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "14px 0",
              color: "#9ca3af",
              fontSize: 13,
            }}
          >
            No overrides — all decks use trip-level prices
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginBottom: 14,
            }}
          >
            {rows.map((p) => {
              const def = defaults[p.deckId];
              const isDeleting = delLoadingKey === `${tripDateId}_${p.deckId}`;
              return (
                <div
                  key={p.id || p.deckId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: "10px 14px",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <div>
                    <strong style={{ fontSize: 13 }}>
                      {p.deck?.name || def?.name || p.deckId}
                    </strong>
                    {def && (
                      <div
                        style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}
                      >
                        Trip default: A: EGP{" "}
                        {Number(def.adultPrice).toLocaleString()} / C: EGP{" "}
                        {Number(def.childPrice).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <Tag
                        color="orange"
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          padding: "1px 8px",
                          margin: 0,
                        }}
                      >
                        A: EGP {Number(p.adultPrice).toLocaleString()}
                      </Tag>
                      <Tag
                        color="cyan"
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          padding: "1px 8px",
                          margin: 0,
                        }}
                      >
                        C: EGP {Number(p.childPrice).toLocaleString()}
                      </Tag>
                    </div>
                    {canDeletePricing && (
                      <Tooltip title="Remove — trip default applies">
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          loading={isDeleting}
                          onClick={() =>
                            handleDeletePricing(tripDateId, p.deckId)
                          }
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {(trip?.tripDecks || []).length > 0 && !isLoading && (
          <>
            <Divider style={{ margin: "10px 0 10px" }} />
            <div
              style={{
                fontSize: 11,
                color: "#9ca3af",
                fontWeight: 600,
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: ".04em",
              }}
            >
              Effective prices on this date
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(trip?.tripDecks || []).map((td) => {
                const override = rows.find((p) => p.deckId === td.deckId);
                const adultPrice = override
                  ? Number(override.adultPrice)
                  : Number(td.adultPrice);
                const childPrice = override
                  ? Number(override.childPrice)
                  : Number(td.childPrice);
                const hasOverride = !!override;
                return (
                  <div
                    key={td.id}
                    style={{
                      background: hasOverride ? "#fff7ed" : "#FDF3E3",
                      border: `1px solid ${
                        hasOverride ? "#fed7aa" : "#F5ECD7"
                      }`,
                      borderRadius: 8,
                      padding: "6px 12px",
                      fontSize: 12,
                      flex: isMobile ? "1 1 calc(50% - 8px)" : "auto",
                    }}
                  >
                    <div style={{ color: "#374151", fontWeight: 600 }}>
                      {td.deck?.name}
                    </div>
                    <div
                      style={{
                        color: hasOverride ? "#c2410c" : "#7B4D12",
                        fontWeight: 700,
                        marginTop: 1,
                      }}
                    >
                      A: EGP {adultPrice.toLocaleString()} / C: EGP{" "}
                      {childPrice.toLocaleString()}
                      {hasOverride && (
                        <span
                          style={{ fontSize: 10, marginLeft: 4, opacity: 0.7 }}
                        >
                          (override)
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  // ── Schedule table columns for desktop ────────────────────────────────────────────────
  const scheduleColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d, record) => (
        <div>
          <strong>{d}</strong>
          {record.notes && record.notes.includes("Manually added") && (
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              Added manually
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Time",
      key: "time",
      render: (_, r) =>
        r.startTime && r.endTime ? (
          <Tag color="volcano">
            {r.startTime} – {r.endTime}
          </Tag>
        ) : (
          <span style={{ color: "#9ca3af", fontSize: 12 }}>
            {trip?.startTime} – {trip?.endTime}
          </span>
        ),
    },
    {
      title: "Price Overrides",
      key: "overrides",
      render: (_, r) => {
        const count = (r.deckPricings || []).length;
        const isOpen = expandedKeys.includes(r.id);
        return (
          <Button
            type="text"
            size="small"
            icon={<DollarOutlined />}
            onClick={() => handleTogglePricing(r.id)}
            style={{
              color: isOpen ? "#7B4D12" : count > 0 ? "#d97706" : "#9ca3af",
              fontWeight: count > 0 ? 600 : 400,
              background: isOpen ? "#FDF3E3" : "transparent",
              borderRadius: 6,
              padding: "0 8px",
            }}
          >
            {count > 0
              ? `${count} override${count > 1 ? "s" : ""}`
              : "Set prices"}
          </Button>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag color={s === "active" ? "green" : "red"}>{s?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 190,
      render: (_, r) => {
        const canEdit = canEditSchedules;
        const canCancel = canEditSchedules || canDeleteSchedules;

        if (!canEdit && !canCancel) return null;

        return (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {canEdit && (
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openDateModal(r)}
              >
                Time
              </Button>
            )}
            {canCancel &&
              (r.status === "active" ? (
                <Button
                  size="small"
                  danger
                  onClick={() => handleCancelDate(r.id)}
                >
                  Cancel
                </Button>
              ) : (
                <Button size="small" onClick={() => handleRestoreDate(r.id)}>
                  Restore
                </Button>
              ))}
          </div>
        );
      },
    },
  ];

  // Show loading while checking permissions
  if (isCheckingPermission) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div className="text-center py-5">
              <Spin size="large" />
              <p className="mt-2">Checking permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Permission check for viewing the page
  if (!canViewTrips) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to view trip details.</p>
                  <p className="mt-2">
                    <strong>Required permission:</strong> trips.view
                  </p>
                  <p className="mt-2">
                    <strong>Your permissions:</strong>{" "}
                    {userPermissions.length > 0
                      ? userPermissions.join(", ")
                      : "None"}
                  </p>
                </div>
              }
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page header */}
          <div className="page-header">
            <div
              className="content-page-header"
              style={{
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "flex-start" : "center",
                gap: isMobile ? 12 : 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => history.push("/trips")}
                  size={isMobile ? "small" : "middle"}
                >
                  Back
                </Button>
                <h5 style={{ margin: 0 }}>Trip Details</h5>
              </div>
              {trip && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {canEditTrips && (
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => history.push(`/trips/${id}/edit`)}
                      size={isMobile ? "small" : "middle"}
                    >
                      Edit Trip
                    </Button>
                  )}
                  {canDeleteTrips && trip.status === "active" ? (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeactivate}
                      size={isMobile ? "small" : "middle"}
                    >
                      Delete
                    </Button>
                  ) : canEditTrips && trip.status !== "active" ? (
                    <Button
                      icon={<CheckOutlined />}
                      onClick={handleActivate}
                      size={isMobile ? "small" : "middle"}
                    >
                      Activate
                    </Button>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* View Only Mode Alert */}
          {!canEditTrips &&
            !canDeleteTrips &&
            !canCreateSchedules &&
            !canEditSchedules &&
            !canCreatePricing &&
            !canDeletePricing && (
              <Alert
                message="View Only Mode"
                description="You have view-only access to this trip. To modify trip details, schedules, or pricing, please contact an administrator."
                type="info"
                showIcon
                className="mb-4"
              />
            )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin size="large" />
            </div>
          ) : !trip ? (
            <Alert message="Trip not found." type="error" />
          ) : (
            <Row gutter={[16, 16]}>
              {/* ── Left: Trip info + Deck pricing ── */}
              <Col xs={24} lg={10}>
                <Card
                  title="Trip Info"
                  style={{ borderRadius: 12, marginBottom: 16 }}
                  bodyStyle={{ padding: isMobile ? "16px" : "24px" }}
                >
                  <Descriptions
                    column={1}
                    size={isMobile ? "small" : "default"}
                  >
                    <Descriptions.Item label="Name">
                      <strong>{trip.name}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Description">
                      {trip.description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Period">
                      {trip.startDate} → {trip.endDate}
                    </Descriptions.Item>
                    <Descriptions.Item label="Default Time">
                      {trip.startTime} – {trip.endTime}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag
                        color={trip.status === "active" ? "green" : "default"}
                      >
                        {trip.status?.toUpperCase()}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* Deck pricing card */}
                <Card
                  title="Deck Pricing"
                  extra={
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>
                      Trip-level defaults
                    </span>
                  }
                  style={{ borderRadius: 12 }}
                  bodyStyle={{ padding: isMobile ? "16px" : "24px" }}
                >
                  {(trip.tripDecks || []).map((td) => {
                    const isEditing = !!editingDecks[td.id];
                    const editVals = editingDecks[td.id] || {};
                    const isSaving = savingDeckId === td.id;
                    return (
                      <div
                        key={td.id}
                        style={{
                          padding: "14px 0",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            marginBottom: isEditing ? 12 : 0,
                            flexDirection:
                              isMobile && isEditing ? "column" : "row",
                            gap: isMobile && isEditing ? 12 : 0,
                          }}
                        >
                          <div>
                            <strong style={{ fontSize: 14 }}>
                              {td.deck?.name}
                            </strong>
                            {!isEditing && (
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#9ca3af",
                                  marginTop: 2,
                                }}
                              >
                                <TeamOutlined style={{ marginRight: 4 }} />
                                {td.capacity} seats
                                <span
                                  style={{ margin: "0 8px", color: "#e5e7eb" }}
                                >
                                  |
                                </span>
                                <DollarOutlined style={{ marginRight: 4 }} />
                                A: EGP {Number(
                                  td.adultPrice,
                                ).toLocaleString()}{" "}
                                / C: EGP{" "}
                                {Number(td.childPrice).toLocaleString()}
                              </div>
                            )}
                          </div>
                          {!isEditing ? (
                            canEditTrips && (
                              <Button
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => startEditDeck(td)}
                              >
                                Edit
                              </Button>
                            )
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                gap: 6,
                                width: isMobile ? "100%" : "auto",
                              }}
                            >
                              <Button
                                size="small"
                                type="primary"
                                style={{
                                  backgroundColor: "#7B4D12",
                                  borderColor: "#7B4D12",
                                }}
                                icon={<SaveOutlined />}
                                loading={isSaving}
                                onClick={() => saveDeckPricing(td)}
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                icon={<CloseOutlined />}
                                disabled={isSaving}
                                onClick={() => cancelEditDeck(td.id)}
                              >
                                Cancel
                              </Button>
                            </div>
                          )}
                        </div>
                        {isEditing && (
                          <div
                            style={{
                              display: "flex",
                              gap: 12,
                              flexDirection: isMobile ? "column" : "row",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  marginBottom: 4,
                                  textTransform: "uppercase",
                                  letterSpacing: ".04em",
                                }}
                              >
                                Adult price
                              </div>
                              <InputNumber
                                style={{ width: "100%" }}
                                min={1}
                                value={editVals.adultPrice}
                                onChange={(v) =>
                                  setEditingDecks((prev) => ({
                                    ...prev,
                                    [td.id]: { ...prev[td.id], adultPrice: v },
                                  }))
                                }
                                disabled={isSaving}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  marginBottom: 4,
                                  textTransform: "uppercase",
                                  letterSpacing: ".04em",
                                }}
                              >
                                Child price
                              </div>
                              <InputNumber
                                style={{ width: "100%" }}
                                min={1}
                                value={editVals.childPrice}
                                onChange={(v) =>
                                  setEditingDecks((prev) => ({
                                    ...prev,
                                    [td.id]: { ...prev[td.id], childPrice: v },
                                  }))
                                }
                                disabled={isSaving}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  marginBottom: 4,
                                  textTransform: "uppercase",
                                  letterSpacing: ".04em",
                                }}
                              >
                                Capacity
                              </div>
                              <InputNumber
                                style={{ width: "100%" }}
                                min={1}
                                value={editVals.capacity}
                                onChange={(v) =>
                                  setEditingDecks((prev) => ({
                                    ...prev,
                                    [td.id]: { ...prev[td.id], capacity: v },
                                  }))
                                }
                                disabled={isSaving}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Card>
              </Col>

              {/* ── Right: Schedule dates ── */}
              <Col xs={24} lg={14}>
                <Card
                  title={
                    <span>
                      Schedule Dates{" "}
                      <Tag style={{ marginLeft: 6 }}>{schedules.length}</Tag>
                    </span>
                  }
                  extra={
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {canCreateSchedules && !isMobile && (
                        <Button
                          type="primary"
                          style={{
                            backgroundColor: "#7B4D12",
                            borderColor: "#7B4D12",
                          }}
                          icon={<PlusOutlined />}
                          onClick={() => setAddDateModal(true)}
                        >
                          Add Dates
                        </Button>
                      )}
                      {!isMobile && (
                        <span style={{ fontSize: 12, color: "#9ca3af" }}>
                          <DollarOutlined style={{ marginRight: 4 }} />
                          Click "Set prices" on any date
                        </span>
                      )}
                    </div>
                  }
                  style={{ borderRadius: 12 }}
                  bodyStyle={{ padding: isMobile ? "12px" : "24px" }}
                >
                  {isMobile ? (
                    // Mobile view: Card-based schedule list
                    <>
                      {schedLoading ? (
                        <div style={{ textAlign: "center", padding: "40px 0" }}>
                          <Spin />
                        </div>
                      ) : schedules.length === 0 ? (
                        <Empty description="No schedule dates found" />
                      ) : (
                        <>
                          {canCreateSchedules && (
                            <Button
                              type="primary"
                              style={{
                                backgroundColor: "#7B4D12",
                                borderColor: "#7B4D12",
                                marginBottom: 16,
                                width: "100%",
                              }}
                              icon={<PlusOutlined />}
                              onClick={() => setAddDateModal(true)}
                            >
                              Add Dates
                            </Button>
                          )}
                          <List
                            dataSource={schedules}
                            renderItem={(schedule) => (
                              <ScheduleCard
                                schedule={schedule}
                                onPress={(s) => {
                                  setSelectedScheduleForMobile(s);
                                  setMobileDrawerOpen(true);
                                }}
                              />
                            )}
                          />
                          <ScheduleDetailDrawer
                            schedule={selectedScheduleForMobile}
                            open={mobileDrawerOpen}
                            onClose={() => setMobileDrawerOpen(false)}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    // Desktop view: Table layout
                    <Table
                      columns={scheduleColumns}
                      dataSource={schedules}
                      rowKey="id"
                      loading={schedLoading}
                      size="small"
                      scroll={{ x: true }}
                      pagination={{
                        pageSize: 15,
                        showSizeChanger: false,
                        showTotal: (t) => `${t} dates`,
                      }}
                      expandable={{
                        expandedRowKeys: expandedKeys,
                        rowExpandable: () => true,
                        expandedRowRender: renderPricingPanel,
                        expandIcon: () => null,
                        columnWidth: 0,
                      }}
                    />
                  )}
                </Card>
              </Col>
            </Row>
          )}
        </div>
      </div>

      {/* ── Edit Date Time Modal ── */}
      {canEditSchedules && (
        <Modal
          title={
            <span>
              <EditOutlined style={{ marginRight: 8, color: "#7B4D12" }} />
              Edit Date — {dateTarget?.date}
            </span>
          }
          open={dateModal}
          onOk={handleSaveDate}
          onCancel={() => {
            setDateModal(false);
            setDateTarget(null);
            dateForm.resetFields();
          }}
          confirmLoading={dateLoading}
          okText="Save"
          width={460}
          destroyOnClose
        >
          <Alert
            message="Override departure/return times for this date only. Leave blank to use trip defaults."
            type="info"
            showIcon
            className="mb-4"
          />
          <Form form={dateForm} layout="vertical" requiredMark={false}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Start Time" name="startTime">
                  <TimePicker
                    format="HH:mm"
                    style={{ width: "100%" }}
                    minuteStep={5}
                    placeholder={trip?.startTime || "HH:mm"}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="End Time" name="endTime">
                  <TimePicker
                    format="HH:mm"
                    style={{ width: "100%" }}
                    minuteStep={5}
                    placeholder={trip?.endTime || "HH:mm"}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Notes" name="notes">
              <Input.TextArea
                rows={2}
                placeholder="e.g. Public holiday, special event…"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Form>
          {dateTarget && (
            <div
              style={{
                background: "#f8fafc",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 12,
                color: "#6b7280",
              }}
            >
              <strong>Trip defaults:</strong> {trip?.startTime} –{" "}
              {trip?.endTime}
            </div>
          )}
        </Modal>
      )}

      {/* ── Add Date Modal ── */}
      {canCreateSchedules && (
        <Modal
          title={
            <span>
              <CalendarOutlined style={{ marginRight: 8, color: "#7B4D12" }} />
              Add Dates to Schedule
            </span>
          }
          open={addDateModal}
          onOk={handleAddDates}
          onCancel={() => {
            setAddDateModal(false);
            setSelectedDate(null);
            setSelectedDateRange(null);
            setAddDateNotes("");
            addDateForm.resetFields();
          }}
          confirmLoading={addDateLoading}
          okText="Add Dates"
          width={520}
          destroyOnClose
          bodyStyle={{ padding: isMobile ? "16px" : "24px" }}
        >
          <Alert
            message="Add additional dates beyond the original trip period. These dates will use the trip's default pricing unless overridden."
            type="info"
            showIcon
            className="mb-4"
          />

          <Form form={addDateForm} layout="vertical">
            <Form.Item label="Date Type" required>
              <Select
                value={addDateType}
                onChange={(value) => {
                  setAddDateType(value);
                  setSelectedDate(null);
                  setSelectedDateRange(null);
                }}
              >
                <Option value="single">Single Date</Option>
                <Option value="range">Date Range</Option>
              </Select>
            </Form.Item>

            {addDateType === "single" && (
              <Form.Item label="Select Date" required>
                <DatePicker
                  style={{ width: "100%" }}
                  value={selectedDate}
                  onChange={setSelectedDate}
                  disabledDate={(current) => {
                    return current && isDateExists(current);
                  }}
                />
                {selectedDate && isDateExists(selectedDate) && (
                  <div style={{ fontSize: 12, color: "#ff4d4f", marginTop: 4 }}>
                    This date already exists in the schedule
                  </div>
                )}
              </Form.Item>
            )}

            {addDateType === "range" && (
              <Form.Item label="Select Date Range" required>
                <RangePicker
                  style={{ width: "100%" }}
                  value={selectedDateRange}
                  onChange={setSelectedDateRange}
                  disabledDate={(current) => {
                    return current && isDateExists(current);
                  }}
                />
              </Form.Item>
            )}

            <Form.Item label="Notes (Optional)">
              <Input.TextArea
                rows={2}
                value={addDateNotes}
                onChange={(e) => setAddDateNotes(e.target.value)}
                placeholder="e.g., Added for special event, holiday season..."
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Form>

          <div
            style={{
              background: "#f8fafc",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: 12,
              color: "#6b7280",
              marginTop: 8,
            }}
          >
            <strong>Trip defaults:</strong> {trip?.startTime} – {trip?.endTime}
            <br />
            <strong>Note:</strong> Added dates will use the trip's default deck
            pricing unless you set price overrides.
          </div>
        </Modal>
      )}

      {/* ── Add Price Override Modal ── */}
      {canCreatePricing && (
        <Modal
          title={
            <span>
              <DollarOutlined style={{ marginRight: 8, color: "#d97706" }} />
              Add Price Override
              {addModal.tripDateId &&
                schedules.find((s) => s.id === addModal.tripDateId) && (
                  <span
                    style={{
                      fontSize: 12,
                      color: "#9ca3af",
                      fontWeight: 400,
                      marginLeft: 8,
                    }}
                  >
                    —{" "}
                    {schedules.find((s) => s.id === addModal.tripDateId)?.date}
                  </span>
                )}
            </span>
          }
          open={addModal.open}
          onOk={handleAddPricing}
          onCancel={() => {
            setAddModal({ open: false, tripDateId: null });
            setAddForm({ deckId: "", adultPrice: "", childPrice: "" });
          }}
          confirmLoading={addLoading}
          okText="Save Override"
          width={420}
          destroyOnClose
          bodyStyle={{ padding: isMobile ? "16px" : "24px" }}
        >
          <Alert
            message="These prices apply only to this specific date, overriding the trip-level prices for that day only."
            type="info"
            showIcon
            className="mb-4"
          />

          <div className="input-block mb-3">
            <label
              style={{
                fontWeight: 600,
                fontSize: 13,
                display: "block",
                marginBottom: 6,
              }}
            >
              Deck <span className="text-danger">*</span>
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select deck"
              value={addForm.deckId || undefined}
              onChange={(v) => setAddForm((f) => ({ ...f, deckId: v }))}
              allowClear
            >
              {availableDecks(addModal.tripDateId).map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.name}
                  <span
                    style={{ color: "#9ca3af", marginLeft: 8, fontSize: 11 }}
                  >
                    (A: {Number(d.adultPrice).toLocaleString()} / C:{" "}
                    {Number(d.childPrice).toLocaleString()})
                  </span>
                </Option>
              ))}
            </Select>
            {availableDecks(addModal.tripDateId).length === 0 && (
              <div style={{ fontSize: 12, color: "#d97706", marginTop: 4 }}>
                All decks already have overrides for this date.
              </div>
            )}
          </div>

          <div className="row">
            <div className="col-6">
              <div className="input-block mb-3">
                <label
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Adult Price (EGP) <span className="text-danger">*</span>
                </label>
                <InputNumber
                  style={{ width: "100%" }}
                  min={1}
                  placeholder="e.g. 1200"
                  value={addForm.adultPrice || undefined}
                  onChange={(v) => setAddForm((f) => ({ ...f, adultPrice: v }))}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="input-block mb-3">
                <label
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Child Price (EGP) <span className="text-danger">*</span>
                </label>
                <InputNumber
                  style={{ width: "100%" }}
                  min={1}
                  placeholder="e.g. 600"
                  value={addForm.childPrice || undefined}
                  onChange={(v) => setAddForm((f) => ({ ...f, childPrice: v }))}
                />
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
            All other dates keep the trip-level prices.
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TripDetail;
